import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/** How far one arrow key press moves the column's edge, in px. */
const KEY_STEP_PX = 16;

type Props = {
    columnId: string;
    /** The column's plain-text name, which the handle's own name carries. */
    label: string;
    /** The column's width now and its limits, in px: the separator's value. */
    value?: { now: number; min: number; max: number };
    onResizeStart: (event: React.PointerEvent<HTMLElement>, columnId: string) => void;
    onResizeMove: (event: React.PointerEvent<HTMLElement>) => void;
    onResizeEnd: (event: React.PointerEvent<HTMLElement>) => void;
    onResizeReset: (columnId: string) => void;
    onResizeBy: (columnId: string, delta: number) => void;
};

/**
 * Drag divider on the right edge of a managed v8 DataTable header cell.
 *
 * Pointer events cover mouse and touch in one path; pointer capture on the handle (taken in
 * `onResizeStart`) keeps move/up events flowing here during the drag. The header `<th>` is
 * `position: relative` (see `dataTable.module.css` `.thead :where(th)`), which is a containing
 * block for absolute children, so no extra positioning is needed on the cell. Sits at z-[11]:
 * above the sticky-column header cells (10), under the loading overlay (12).
 *
 * A focusable separator is a splitter: it reports the column's width as its value, the arrow keys move
 * the edge a step at a time, and Home or Enter go back to the column's own width, as a double-click does.
 *
 * Geometry never reaches React: the pointer handlers only write the container's `--utw-*` CSS variables
 * (via `useDataTableLayout`), so a drag does not re-render the memoized table body.
 */
const DataTableColumnResizeHandle = ({
    columnId,
    label,
    value,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
    onResizeReset,
    onResizeBy,
}: Props) => (
    <div
        role="separator"
        tabIndex={0}
        aria-label={t('table.resize-column', { column: label })}
        aria-orientation="vertical"
        aria-valuenow={value?.now}
        aria-valuemin={value?.min}
        aria-valuemax={value?.max}
        className={cn(
            'group/resize absolute top-0 right-0 z-[11] flex h-full w-2 cursor-col-resize touch-none items-center justify-end select-none'
        )}
        onPointerDown={(event) => onResizeStart(event, columnId)}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => {
            event.stopPropagation();
            onResizeReset(columnId);
        }}
        onKeyDown={(event) => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                onResizeBy(columnId, event.key === 'ArrowRight' ? KEY_STEP_PX : -KEY_STEP_PX);
            } else if (event.key === 'Enter' || event.key === 'Home') {
                onResizeReset(columnId);
            } else {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
        }}
    >
        {/* Centered grip, always visible in classic; the new design shows it on hover and on keyboard focus
            (CSS module). The full-height parent stays the grab target. */}
        <div
            className={cn(
                'bg-divider/60 h-6 w-1 rounded-full transition-colors',
                'group-hover/resize:bg-grey-400 group-data-[resizing=true]/resize:bg-primary'
            )}
        />
    </div>
);

export default DataTableColumnResizeHandle;
